import React, { memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { initialPortfolioFiltersQuery } from '@pages/Trade/Portfolio/Portfolio.page'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CalendarPopup } from '@ui/v2/Calendar/CalendarPopup'
import { FilterHorizontalScroll } from '@ui/v2/TableFilter/FilterHorizontalScroll'

import { useWindowResize } from '@utils/domUtils'

import type { DxTradeOrderType } from '@model/DxTrade.types'

import { type DxtradePositionEffect } from '../../utils/constants'
import { PortfolioFilters } from './PortfolioFilters'

import styles from '../Portfolio.page.module.scss'

type OpenFilters = {
  side?: 'BUY' | 'SELL'
  openTimeFrom?: string
  openTimeTo?: string
}

export type PendingFiltersType = {
  side?: 'BUY' | 'SELL'
  ofType?: DxTradeOrderType
  issuedFrom?: string
  issuedTo?: string
}

type Props = {
  isOpenTab: boolean
  activePath: string
  openFilters: OpenFilters
  currentTabFilters: PendingFiltersType
  setOpenFilters: (value: OpenFilters) => void
  setOrdersBasicFilters: (value: {
    side?: 'BUY' | 'SELL'
    ofType?: DxTradeOrderType | undefined
    positionEffect?: DxtradePositionEffect
  }) => void
  setOrdersDateFilters: (value: { issuedFrom?: string; issuedTo?: string }) => void
  onFiltersChange: (filters: PortfolioFiltersType) => void
}

export interface PortfolioFiltersType {
  side?: 'BUY' | 'SELL'
  openTimeFrom?: string
  openTimeTo?: string
}

const FiltersRowComponent: React.FC<Props> = (props) => {
  const { isOpenTab, activePath, openFilters, currentTabFilters } = props
  const { setOpenFilters, setOrdersBasicFilters, setOrdersDateFilters, onFiltersChange } = props

  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const handleOpenDateConfirm = useCallback(
    (range: [Date, Date]) => {
      const [start, end] = range

      const startOfDay = start ? new Date(start) : null
      if (startOfDay) {
        startOfDay.setHours(0, 0, 0, 0)
      }
      const endOfDay = end ? new Date(end) : null
      if (endOfDay) {
        endOfDay.setHours(23, 59, 59, 999)
      }

      setOpenFilters({
        openTimeFrom: startOfDay?.toISOString(),
        openTimeTo: endOfDay?.toISOString(),
      })
    },
    [setOpenFilters]
  )

  const handleOrdersDateConfirm = useCallback(
    (range: [Date, Date]) => {
      const [start, end] = range

      const startOfDay = start ? new Date(start) : null
      if (startOfDay) {
        startOfDay.setHours(0, 0, 0, 0)
      }

      const endOfDay = end ? new Date(end) : null
      if (endOfDay) {
        endOfDay.setHours(23, 59, 59, 999)
      }

      setOrdersDateFilters({
        issuedFrom: startOfDay?.toISOString(),
        issuedTo: endOfDay?.toISOString(),
      })
    },
    [setOrdersDateFilters]
  )

  const handleClearFilters = useCallback(() => {
    if (isOpenTab) {
      onFiltersChange(initialPortfolioFiltersQuery())
    } else {
      setOrdersBasicFilters({
        side: undefined,
        ofType: undefined,
        positionEffect: undefined,
      })
      setOrdersDateFilters({ issuedFrom: undefined, issuedTo: undefined })
    }
  }, [isOpenTab, activePath, onFiltersChange, setOrdersBasicFilters, setOrdersDateFilters])

  const hasDateFrom = isOpenTab ? openFilters.openTimeFrom : currentTabFilters.issuedFrom
  const hasDateTo = isOpenTab ? openFilters.openTimeTo : currentTabFilters.issuedTo
  const dateHandler = isOpenTab ? handleOpenDateConfirm : handleOrdersDateConfirm
  const resetDateHandler = isOpenTab
    ? () => setOpenFilters({ openTimeFrom: undefined, openTimeTo: undefined })
    : () => setOrdersDateFilters({ issuedFrom: undefined, issuedTo: undefined })

  const filterSearchFilters = isOpenTab ? openFilters : currentTabFilters
  const filterSetSearchFilters = isOpenTab ? setOpenFilters : setOrdersBasicFilters

  return (
    <div className={isMobile ? styles.badgesArea : styles.filtersLeft}>
      <FilterHorizontalScroll>
        <CalendarPopup
          onConfirm={(range) => dateHandler(range)}
          startDate={hasDateFrom ? new Date(hasDateFrom) : undefined}
          endDate={hasDateTo ? new Date(hasDateTo) : undefined}
          placement='bottom'
          maxDate={new Date()}
        >
          <Button.Secondary
            size='md'
            iconRight={ChevronIcon.Down}
            endDecorator={
              (hasDateFrom || hasDateTo) && (
                <Chip
                  selected
                  size='xs'
                  onDelete={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    resetDateHandler()
                  }}
                >
                  {hasDateFrom && hasDateTo ? '2' : '1'}
                </Chip>
              )
            }
          >
            {t('Date')}
          </Button.Secondary>
        </CalendarPopup>
        <PortfolioFilters
          includeOrderType={isOpenTab ? false : activePath === 'pending'}
          includeEffectFilter={activePath === 'history'}
          searchFilters={filterSearchFilters}
          setSearchFilters={filterSetSearchFilters}
        />
      </FilterHorizontalScroll>

      <div className={styles.clearButton}>
        <Button size='md' variant='secondary' onClick={handleClearFilters}>
          {t('Clear Filters')}
        </Button>
      </div>
    </div>
  )
}

export const FiltersRow = memo(FiltersRowComponent)
